/**
 * RFM Export Button Component
 *
 * Provides CSV export functionality for RFM data:
 * - Export individual segment customers
 * - Export complete RFM analysis summary
 * - Loading states during export
 * - Automatic file download
 * - Error handling
 */

import { useState } from "react";
import { Button } from "@/frontend/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { RFMAnalyticsService } from "@/frontend/services/rfm-analytics";
import type { RFMSegment } from "@/shared/types/rfm-analytics";
import { EXPORT_TRANSLATIONS } from "@/shared/lib/rfm-translations";

interface RFMExportButtonProps {
  /** Export type: 'segment' or 'analysis' */
  type: 'segment' | 'analysis';
  /** Segment name (required for type='segment') */
  segmentName?: RFMSegment;
  /** Period in YYYY-MM format */
  period?: string;
  /** Button text */
  label?: string;
  /** Button variant */
  variant?: 'default' | 'outline' | 'ghost';
  /** Button size */
  size?: 'default' | 'sm' | 'lg';
}

export function RFMExportButton({
  type,
  segmentName,
  period,
  label,
  variant = 'outline',
  size = 'default'
}: RFMExportButtonProps) {

  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const api = useAuthenticatedApi();

  // Get button label with Thai translation
  const getButtonLabel = () => {
    if (isExporting) return EXPORT_TRANSLATIONS.exporting;
    if (label) return label;
    return type === 'segment' ? EXPORT_TRANSLATIONS.exportSegment : EXPORT_TRANSLATIONS.exportAnalysis;
  };

  // Download CSV file
  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Generate filename
  const getFilename = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const periodStr = period ? `_${period}` : '';

    if (type === 'segment' && segmentName) {
      const segmentSlug = segmentName.toLowerCase().replace(/[^a-z0-9]/g, '-');
      return `rfm-segment-${segmentSlug}${periodStr}_${timestamp}.csv`;
    } else {
      return `rfm-analysis${periodStr}_${timestamp}.csv`;
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);

      const service = new RFMAnalyticsService(api);
      let csvContent: string;

      if (type === 'segment') {
        if (!segmentName) {
          throw new Error('Segment name is required for segment export');
        }
        csvContent = await service.exportSegmentToCSV(segmentName, { period });
      } else {
        csvContent = await service.exportAnalysisToCSV({ period });
      }

      const filename = getFilename();
      downloadCSV(csvContent, filename);

    } catch (err: any) {
      console.error('Export error:', err);
      setError(err.message || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="inline-block">
      <Button
        variant={variant}
        size={size}
        onClick={handleExport}
        disabled={isExporting}
      >
        {isExporting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Download className="mr-2 h-4 w-4" />
        )}
        {getButtonLabel()}
      </Button>

      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Export menu with multiple export options
 */
interface RFMExportMenuProps {
  segmentName?: RFMSegment;
  period?: string;
}

export function RFMExportMenu({ segmentName, period }: RFMExportMenuProps) {
  return (
    <div className="flex items-center gap-2">
      {segmentName && (
        <RFMExportButton
          type="segment"
          segmentName={segmentName}
          period={period}
          label={EXPORT_TRANSLATIONS.exportSegment}
          size="sm"
        />
      )}

      <RFMExportButton
        type="analysis"
        period={period}
        label={EXPORT_TRANSLATIONS.exportAll}
        variant="default"
        size="sm"
      />
    </div>
  );
}
